import io from "socket.io-client";
import api from "../services/api";

// Quantas vezes seguidas tentamos renovar o token após o servidor recusar a sessão.
const MAX_AUTH_RETRIES = 3;

// Token vigente no momento da chamada. api.defaults é atualizado em todo
// refresh (useAuth); localStorage é o fallback.
function currentAuthHeader() {
  const fromDefaults = api.defaults.headers.Authorization;
  if (fromDefaults) return fromDefaults;
  const stored = localStorage.getItem("token");
  if (!stored) return null;
  try {
    return `Bearer ${JSON.parse(stored)}`;
  } catch (_) {
    return null;
  }
}

// Renova via cookie jrt (mesmo endpoint do interceptor do useAuth).
async function refreshAuthHeader() {
  const { data } = await api.post("/auth/refresh_token");
  if (!data?.token) return null;
  localStorage.setItem("token", JSON.stringify(data.token));
  api.defaults.headers.Authorization = `Bearer ${data.token}`;
  return api.defaults.headers.Authorization;
}

class SocketWorker {
  constructor(companyId, userId) {
    const existing = SocketWorker.instance;
    // Troca de sessão sem reload: nunca reaproveitar socket de outro usuário/empresa.
    // Só compara quando o chamador informa os dois (Chat/ChatPopover chamam sem eles).
    if (
      existing &&
      companyId &&
      userId &&
      (String(existing.companyId) !== String(companyId) || String(existing.userId) !== String(userId))
    ) {
      existing.disconnect();
    }

    if (!SocketWorker.instance) {
      this.companyId = companyId;
      this.userId = userId;
      this.socket = null;
      this.eventListeners = {};
      this.authRetries = 0;
      this.refreshing = false;
      this.closed = false;
      this.stableTimer = null;
      this.configureSocket();
      SocketWorker.instance = this;
    }

    return SocketWorker.instance;
  }

  configureSocket() {
    this.socket = io(`${process.env.REACT_APP_BACKEND_URL}/${this.companyId}`, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      query: { userId: this.userId, token: currentAuthHeader() }
    });

    // Reconexão automática (queda de rede/transporte): relê o token vigente
    // antes de cada tentativa — o Manager cria um engine novo a partir de opts.query.
    this.socket.io.on("reconnect_attempt", () => {
      if (!this.socket) return;
      this.socket.io.opts.query = { ...this.socket.io.opts.query, token: currentAuthHeader() };
    });

    this.socket.on("connect", () => {
      // Só considera a conexão estável (e zera o contador) depois que ela se
      // mantém conectada por um tempo. No socket.io 4.7, quando o servidor
      // recusa a sessão dentro do evento "connection", o CONNECT já foi
      // enviado: o cliente recebe "connect" e, em seguida, "disconnect"
      // ("io server disconnect"). Zerar aqui de imediato nunca deixaria
      // authRetries atingir MAX_AUTH_RETRIES nesse cenário.
      clearTimeout(this.stableTimer);
      this.stableTimer = setTimeout(() => {
        this.authRetries = 0;
      }, 10000);
    });

    this.socket.on("disconnect", reason => {
      clearTimeout(this.stableTimer);
      // "io server disconnect" = servidor recusou a sessão (token expirado/inválido).
      // Nesse caso o socket.io NÃO reconecta sozinho: renova o token e tenta de novo, com limite.
      // Demais motivos: a reconexão automática + reconnect_attempt acima cuidam.
      if (reason === "io server disconnect") {
        this.reconnectWithFreshToken();
      }
    });
  }

  async reconnectWithFreshToken() {
    if (this.refreshing || !this.socket || this.closed) return;
    if (this.authRetries >= MAX_AUTH_RETRIES) {
      console.warn("Socket: sessão recusada pelo servidor; reconexão suspensa até novo login/reload");
      return;
    }
    this.authRetries += 1;
    this.refreshing = true;
    try {
      const header = await refreshAuthHeader();
      if (!header || !this.socket || this.closed) return;
      this.socket.io.opts.query = { ...this.socket.io.opts.query, token: header };
      const delay = 1000 * this.authRetries;
      setTimeout(() => {
        if (this.socket && !this.closed && !this.socket.connected) this.socket.connect();
      }, delay);
    } catch (_) {
      // Refresh falhou (401/rede): não insiste. O interceptor do axios já trata 401 (logout).
    } finally {
      this.refreshing = false;
    }
  }

  on(event, callback) {
    this.connect();
    if (!this.socket) return;
    this.socket.on(event, callback);
    if (!this.eventListeners[event]) this.eventListeners[event] = [];
    this.eventListeners[event].push(callback);
  }

  emit(event, data) {
    this.connect();
    if (!this.socket) return;
    this.socket.emit(event, data);
  }

  off(event, callback) {
    if (!this.socket || !this.eventListeners[event]) return;
    if (callback) {
      this.socket.off(event, callback);
      this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
    } else {
      this.eventListeners[event].forEach(cb => this.socket.off(event, cb));
      delete this.eventListeners[event];
    }
  }

  disconnect() {
    clearTimeout(this.stableTimer);
    this.closed = true;
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.io.off("reconnect_attempt");
      this.socket.disconnect();
      this.socket = null;
    }
    this.eventListeners = {};
    // Bug anterior: zerava this.instance (propriedade da instância), não o singleton.
    if (SocketWorker.instance === this) SocketWorker.instance = null;
  }

  // Garante socket ativo; uma instância encerrada (logout) nunca ressuscita com token antigo.
  connect() {
    if (!this.socket && !this.closed) this.configureSocket();
  }

  forceReconnect() {}
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
